import React, { HTMLProps, useMemo, useRef } from 'react';
import Persist, { StorageKind } from 'lib/persist';
import { tabSelected, tipoCarrelloSelected } from 'features/carrello/carrelloSlice';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';

import { CarrelloTabEnum } from 'features/carrello/types';
import classNames from 'classnames';
import { useCartType } from 'features/carrello/hooks';
import { useIsomorphicLayoutEffect } from 'hooks';
import { toggleCarrello } from 'features/carrello/actions';
import { useTranslation } from 'hooks/useLingUI';
import { isClientSide } from 'utility/constant';
import { isTruthy } from 'utility/functions';
import { selectIsOpenCarrello, selectNumEsitiEveryTicket } from 'features/carrello/carrelloSelectors';
import styles from './CarrelloTrigger.module.scss';

const localStorage = Persist(StorageKind.local);

export const CarrelloTrigger = ({ className }: HTMLProps<HTMLElement>) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const type = useCartType();

  const isOpen = useTypedSelector(selectIsOpenCarrello);
  const { numEsitiEveryTicket, isActive: isBigliettoActive } = useTypedSelector(selectNumEsitiEveryTicket);

  const btnBigliettoRef = useRef<HTMLButtonElement>(null);
  const btnScommesseRef = useRef<HTMLButtonElement>(null);

  const { numScommesse, isScommesseActive, bigliettoClassName, scommesseClassName } = useMemo(() => {
    let numScommesse = 0;

    if (isClientSide) {
      // Initialize an empty array to store the retrieved data
      // Loop through the keys and read data from local storage
      ['prenotazione-scommessa-codicesessione-sport'].forEach((key) => {
        const item = localStorage.getItem(key);
        if (item) {
          numScommesse++;
        }
      });
    }

    const isScommesseActive = isTruthy(numScommesse);
    const bigliettoClassName = classNames(styles.button, { [styles.bigliettoActive]: isBigliettoActive });
    const scommesseClassName = classNames(styles.button, { [styles.scommessaActive]: isScommesseActive });

    return {
      numScommesse,
      isScommesseActive,
      bigliettoClassName,
      scommesseClassName,
    };
  }, [isBigliettoActive]);

  useIsomorphicLayoutEffect(() => {
    if (btnBigliettoRef.current) {
      btnBigliettoRef.current.className = bigliettoClassName;
    }

    if (btnScommesseRef.current) {
      btnScommesseRef.current.className = scommesseClassName;
    }
  }, [bigliettoClassName, scommesseClassName]);

  const handleClick = async (tab: CarrelloTabEnum) => {
    dispatch(tipoCarrelloSelected(type));
    dispatch(toggleCarrello({ isFullCart: true, isOpen: true }));
    dispatch(tabSelected(tab));
  };

  return (
    <div className={classNames(styles.container, className ?? '', { [styles.hidden]: isOpen })}>
      <button
        ref={btnBigliettoRef}
        key={`biglietto-${numEsitiEveryTicket}-${bigliettoClassName}`}
        type="button"
        className={bigliettoClassName}
        suppressHydrationWarning
        onClick={() => handleClick(CarrelloTabEnum.Biglietto)}
      >
        {t('biglietto', 'tiket')}
        <span className={styles.number} suppressHydrationWarning>
          {isBigliettoActive && numEsitiEveryTicket}
        </span>
      </button>
      <button
        ref={btnScommesseRef}
        key={`scommesse-${numScommesse}-${scommesseClassName}`}
        type="button"
        className={scommesseClassName}
        suppressHydrationWarning
        onClick={() => handleClick(CarrelloTabEnum.Scommesse)}
      >
        {t('scommesse', 'bets')}
        <span className={styles.number} suppressHydrationWarning>
          {isScommesseActive && numScommesse}
        </span>
      </button>
    </div>
  );
};